import { QuoteBuilderStateType } from '@ui/features/quote-builder/types/state';
import {
  IssueAreaType,
  type IssuesType,
  IssueType,
  issueTypes,
} from '@ui/features/quote-builder/types/state/issues';
import { QuoteBuilderOperationType } from '@ui/features/quote-builder/types/state/operation';
import { QuoteLineType } from '@ui/features/quote-builder/types/state/quote-line';
import { QuotePriceType } from '@ui/features/quote-builder/types/state/quote-price';
import { QuoteBuilderRequirementType } from '@ui/features/quote-builder/types/state/requirement';

import { buildIssueId } from './issue-id';

/**
 * Convenience function to throw an error if the quote builder state is missing
 *
 * Use this in selectors that need to access the quote builder state.
 *
 * @param {QuoteBuilderStateType | undefined} quoteBuilder - current state of the quote builder
 * @returns {QuoteBuilderStateType} the quote builder state
 * @throws {Error} if the quote builder state is not found
 */
export function getQuoteBuilderOrThrow(
  quoteBuilder: QuoteBuilderStateType | undefined,
): QuoteBuilderStateType {
  if (!quoteBuilder) {
    throw new Error('Missing quoteBuilder');
  }

  return quoteBuilder;
}

/**
 * Convenience function to throw an error if a quote line is missing
 *
 * Use this in selectors that need to access the quote builder state.
 *
 * @param {QuoteBuilderStateType | undefined} quoteBuilder - current state of the quote builder
 * @param {string} quoteLineUuid - uuid of the quote line to retrieve
 * @returns {QuoteLineType} the quote line with the provided uuid
 * @throws {Error} if the quote line is not found
 */

export function getQuoteLineOrThrow(
  quoteBuilder: QuoteBuilderStateType | undefined,
  quoteLineUuid: string,
): QuoteLineType {
  const state = getQuoteBuilderOrThrow(quoteBuilder);

  if (!state.quoteLines[quoteLineUuid]) {
    throw new Error(`Quote line with uuid ${quoteLineUuid} not found`);
  }

  return state.quoteLines[quoteLineUuid];
}

/**
 * Convenience function to throw an error if a quote price is missing
 *
 * Use this in selectors that need to access the quote builder state.
 *
 * @param {QuoteBuilderStateType | undefined} quoteBuilder - current state of the quote builder
 * @param {string} quoteLineUuid - uuid of the quote line to retrieve
 * @param {string} quotePriceUuid - uuid of the quote price to retrieve
 * @returns {QuotePriceType} the quote price with the provided uuid
 * @throws {Error} if the quote builder is not found
 */

export function getQuotePriceOrThrow(
  quoteBuilder: QuoteBuilderStateType | undefined,
  quoteLineUuid: string,
  quotePriceUuid: string,
): QuotePriceType {
  const quoteLine = getQuoteLineOrThrow(quoteBuilder, quoteLineUuid);

  if (!quoteLine.quotePrices[quotePriceUuid]) {
    throw new Error(`Quote price with uuid ${quotePriceUuid} not found`);
  }

  return quoteLine.quotePrices[quotePriceUuid];
}

/**
 * Convenience function to throw an error if a quote price is missing
 *
 * Use this in selectors that need to access the quote builder state.
 *
 * @param {QuoteLineType} quoteLine - the quote line state
 * @param {string} snapOperationUuid - uuid of the snap operation to retrieve
 *
 * @returns {QuoteBuilderOperationType} the snap operation with the provided uuid
 *
 * @throws {Error} if the snap work order or operation is not found
 */
export function getSnapOperationOrThrow(
  quoteLine: QuoteLineType,
  snapOperationUuid: string,
): QuoteBuilderOperationType {
  if (!quoteLine.snapWorkOrder) {
    throw new Error(
      `Snap work order not found for quote line ${quoteLine.uuid}`,
    );
  }

  if (!quoteLine.snapWorkOrder.snapOperations[snapOperationUuid]) {
    throw new Error(`Snap operation with uuid ${snapOperationUuid} not found`);
  }

  return quoteLine.snapWorkOrder.snapOperations[snapOperationUuid];
}

export function getSnapRequirementOrThrow(
  quoteLine: QuoteLineType,
  snapOperationUuid: string,
  snapRequirementUuid: string,
): QuoteBuilderRequirementType {
  const snapOperation = getSnapOperationOrThrow(quoteLine, snapOperationUuid);

  if (!snapOperation.snapRequirements[snapRequirementUuid]) {
    throw new Error(
      `Snap requirement with uuid ${snapRequirementUuid} not found`,
    );
  }

  return snapOperation.snapRequirements[snapRequirementUuid];
}

export type SelectIssueParams = {
  field?: string;
} & IssueAreaType;

/**
 * Selects issues from the QuoteBuilder state for a given area and type.
 *
 * Use this in selectors that need to access issues in the quote builder state.
 *
 * @param {IssuesType} issues - The current issues state from the QuoteBuilder.
 * @param {SelectIssueParams} params - Parameters for selecting issues, including types and area.
 * @param {IssueOptions[]} params.types - Array of issue types to filter by.
 * @param {string} [params.field] - Optional field name to filter by.
 * @param {string} [params.quoteLineUuid] - Optional quote line UUID to filter by.
 * @param {string} [params.quotePriceUuid] - Optional quote price UUID to filter by.
 * @param {string} [params.snapOperationUuid] - Optional snap operation UUID to filter by.
 * @param {string} [params.snapRequirementUuid] - Optional snap requirement UUID to filter by.
 *
 * @returns {IssueType[]} An array of matching issue objects.
 */
export function getIssues(
  issues: IssuesType,
  params: SelectIssueParams,
): IssueType[] {
  const selectedIssues: IssueType[] = [];

  for (const type of issueTypes) {
    const id = buildIssueId({
      ...params,
      type,
    });

    if (issues[id]) {
      selectedIssues.push(issues[id]);
    }
  }

  return selectedIssues;
}
